import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { useDebounceLoading } from '@/hooks/useDebounceLoading';
import { gqlClient } from '@/lib';
import { createExperienceEventMessagesQueryKey } from '@/utils/queryKeys';
import type {
  GetExperienceEventMessagesQuery,
  GetExperienceEventMessagesQueryVariables,
} from './useExperienceEventMessages.generated';

const query = gql`
  query GetExperienceEventMessages($input: GetEventMessagesInput!) {
    getEventMessages(input: $input) {
      id
      instance
      body
      deliveryTimeType
      hybridDeliveryTime
      hybridRelativeTime
      hybridTimeZone
      relativeTimeToEntryStart
      relativeTimeToEntryStartUnit
      relativeTimeToEntryStartDirection
      sendToEveryone
      sendAt
      status
      recipients {
        id
        displayName
      }
      event {
        id
        title
        emoji
        isAllDay
        start {
          date
          dateTime
          timeZone
        }
      }
    }
  }
`;

export type ExperienceEventMessage =
  GetExperienceEventMessagesQuery['getEventMessages'][number];

// Every message scheduled in the experience (across events), ordered by send
// time ascending.
export const useExperienceEventMessages = () => {
  const { experience } = usePageExperienceContext();

  const { data: messages, isLoading } = useQuery({
    queryKey: createExperienceEventMessagesQueryKey(experience.id),
    select: (data: GetExperienceEventMessagesQuery) => data.getEventMessages,
    queryFn: () =>
      gqlClient.request<
        GetExperienceEventMessagesQuery,
        GetExperienceEventMessagesQueryVariables
      >(query, {
        input: { experienceId: experience.id },
      }),
  });

  const debouncedIsLoading = useDebounceLoading(isLoading, 200);

  return {
    messages: messages ?? [],
    isLoading: debouncedIsLoading,
  };
};
